/** Fallback viewport width below which the row becomes a swipeable carousel, if [data-breakpoint] is absent. */
const DEFAULT_BREAKPOINT = 750;

/** Minimum horizontal drag distance (px) to count as a swipe. */
const SWIPE_THRESHOLD = 40;

/** Minimum accumulated horizontal wheel delta (px) to count as a trackpad swipe. */
const WHEEL_THRESHOLD = 60;

/** Cooldown (ms) after a wheel-triggered slide change, so one gesture doesn't fire multiple times. */
const WHEEL_COOLDOWN = 400;

/**
 * <group-mobile-carousel> turns its `.group-block` children into a
 * swipeable carousel below the [data-breakpoint] width (defaults to
 * 750px), showing exactly one slide at a time (rather than a native
 * scroll container) so there is no dependency on browser
 * scroll-snap/scroll-restoration behavior — which slide is active is
 * plain JS state, not a scroll position. Pagination dots are built from
 * the actual slide elements found in the DOM (not a pre-computed count),
 * so they can never drift out of sync — non-slide children (e.g. a spacer
 * meant for the desktop row) are ignored. At the breakpoint and up,
 * everything reverts to the normal CSS row layout.
 *
 * @extends {HTMLElement}
 */
class GroupMobileCarousel extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLElement[]} */
  #spacers = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {HTMLElement | null} */
  #dotsContainer = null;

  /** @type {boolean} */
  #isCarousel = false;

  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #wheelDeltaX = 0;

  /** @type {boolean} */
  #wheelCooldown = false;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  connectedCallback() {
    const breakpoint = Number(this.dataset.breakpoint) || DEFAULT_BREAKPOINT;
    this.#mobileQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    const children = Array.from(this.children);
    this.#slides = children.filter((el) => el.classList.contains('group-block'));
    this.#spacers = children.filter((el) => !el.classList.contains('group-block'));

    if (this.#slides.length < 2) return;

    this.#mobileQuery.addEventListener('change', this.#onBreakpointChange);
    this.#applyMode();
  }

  disconnectedCallback() {
    this.#mobileQuery?.removeEventListener('change', this.#onBreakpointChange);
    this.#teardownCarousel();
  }

  #onBreakpointChange = () => this.#applyMode();

  #applyMode() {
    if (this.#mobileQuery?.matches) {
      this.#setupCarousel();
    } else {
      this.#teardownCarousel();
    }
  }

  #setupCarousel() {
    if (this.#isCarousel) return;
    this.#isCarousel = true;
    this.#activeIndex = 0;
    this.#wheelDeltaX = 0;
    this.#wheelCooldown = false;

    this.#spacers.forEach((el) => el.style.setProperty('display', 'none'));

    this.style.display = 'block';
    this.style.overflow = 'hidden';

    this.#slides.forEach((slide) => {
      slide.style.width = '100%';
    });

    this.#buildDots();
    this.#render();

    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.addEventListener('wheel', this.#onWheel, { passive: false });
  }

  #teardownCarousel() {
    if (!this.#isCarousel) return;
    this.#isCarousel = false;

    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('wheel', this.#onWheel);

    this.#spacers.forEach((el) => el.style.removeProperty('display'));

    [this.style, ...this.#slides.map((slide) => slide.style)].forEach((style) => {
      style.removeProperty('display');
      style.removeProperty('overflow');
      style.removeProperty('width');
    });

    this.#dotsContainer?.remove();
    this.#dotsContainer = null;
    this.#dots = [];
  }

  #buildDots() {
    this.#dotsContainer?.remove();

    const container = document.createElement('div');
    container.className = 'group-block-carousel-dots';
    container.setAttribute('role', 'tablist');

    this.#dots = this.#slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'group-block-carousel-dot';
      dot.setAttribute('role', 'tab');
      dot.addEventListener('click', () => this.#goTo(index));
      container.appendChild(dot);
      return dot;
    });

    this.after(container);
    this.#dotsContainer = container;
  }

  /** @param {number} index */
  #goTo(index) {
    this.#activeIndex = Math.max(0, Math.min(index, this.#slides.length - 1));
    this.#render();
  }

  #render() {
    this.#slides.forEach((slide, index) => {
      slide.style.display = index === this.#activeIndex ? '' : 'none';
    });
    this.#dots.forEach((dot, index) => dot.classList.toggle('is-active', index === this.#activeIndex));
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#touchStartX = event.touches[0].clientX;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const deltaX = event.changedTouches[0].clientX - this.#touchStartX;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    // Swiping left (negative delta) advances to the next slide.
    this.#goTo(this.#activeIndex + (deltaX < 0 ? 1 : -1));
  };

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    // Ignore mostly-vertical wheel gestures (regular page scroll) so we
    // don't hijack scrolling — only trackpad two-finger horizontal swipes.
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    event.preventDefault();
    if (this.#wheelCooldown) return;

    this.#wheelDeltaX += event.deltaX;
    if (Math.abs(this.#wheelDeltaX) < WHEEL_THRESHOLD) return;

    this.#goTo(this.#activeIndex + (this.#wheelDeltaX > 0 ? 1 : -1));
    this.#wheelDeltaX = 0;
    this.#wheelCooldown = true;
    setTimeout(() => {
      this.#wheelCooldown = false;
    }, WHEEL_COOLDOWN);
  };
}

if (!customElements.get('group-mobile-carousel')) {
  customElements.define('group-mobile-carousel', GroupMobileCarousel);
}
